<script setup lang="ts">
import type { ShareCollectionModalProps } from '.';

import { useEvents } from '~/composables/events/useEvents';
import { useShareCollection } from '~/composables/useShareCollection';
import { MixpanelProps } from '~/core/service/mixpanel';
import messages from '~~/data/shareCollection';
import { createSharingUrlsForMessage } from '~~/utils';

import IconMaterialSymbolsExpandLess from '~icons/material-symbols/expand-less';
import IconMaterialSymbolsExpandMore from '~icons/material-symbols/expand-more';
import IconMdiContentCopy from '~icons/mdi/content-copy';
import IconMdiEmailOutline from '~icons/mdi/email-outline';
import IconRiFacebookCircleLine from '~icons/ri/facebook-circle-line';
import IconRiTwitterXLine from '~icons/ri/twitter-x-line';
import IconRiWhatsappLine from '~icons/ri/whatsapp-line';

import { CopyToastDescription } from '../CopyToastDescription';
import { StackedPropertyImages } from '../StackedPropertyImages';
import { ToggleContainer } from '../ToggleContainer';
import { Button } from '../ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../ui/dialog';
import { useToast } from '../ui/toast';

defineOptions({ name: 'ShareCollectionModal' });

interface SharingOption {
	value: string;
	label: string;
	icon: any;
	isAlwaysVisible: boolean;
	isCopyAction?: boolean;
}

const props = withDefaults(defineProps<ShareCollectionModalProps>(), {
	isOwner: true,
	ownerName: '',
	sectionName: 'user collection'
});

const modelValue = defineModel<boolean>({ required: true });

const { toast } = useToast();

// Get property images for stacked display
const propertyImages = computed(() => {
	if (props.properties && props.properties.length > 0) {
		return props.properties.slice(0, 2).map(property => {
			const firstImage = property.images?.[0];
			return (
				firstImage?.watermarkUrl ||
				firstImage?.url ||
				property.thumbnailUrl ||
				null
			);
		});
	}
	return [];
});

const wishlistIdRef = toRef(props, 'wishlistId');
const isOwnerRef = toRef(props, 'isOwner');
const ownerNameRef = toRef(props, 'ownerName');

const { shareMessage, encodedShareMessage, shareUrl } = useShareCollection(
	wishlistIdRef,
	isOwnerRef,
	ownerNameRef
);

// Dynamic texts based on ownership (static during component lifecycle)
const textKey: 'owner' | 'visitor' = props.isOwner ? 'owner' : 'visitor';
const title = messages.shareCollection[textKey].title;
const description = messages.shareCollection[textKey].description.replace(
	'{ownerName}',
	props.ownerName || ''
);
const footer = messages.shareCollection.common.footer;

const SHARE_LABELS = {
	WHATSAPP: 'WhatsApp',
	COPY_LINK: 'Copiar link',
	TWITTER: 'Twitter',
	FACEBOOK: 'Facebook',
	EMAIL: 'Enviar por e-mail'
} as const;

const sharingOptions = computed<SharingOption[]>(() => {
	const urls = createSharingUrlsForMessage(encodedShareMessage.value);

	return [
		{
			value: urls.whatsapp,
			label: SHARE_LABELS.WHATSAPP,
			icon: IconRiWhatsappLine,
			isAlwaysVisible: true
		},
		{
			value: shareMessage.value,
			label: SHARE_LABELS.COPY_LINK,
			icon: IconMdiContentCopy,
			isAlwaysVisible: true,
			isCopyAction: true
		},
		{
			value: urls.twitter,
			label: SHARE_LABELS.TWITTER,
			icon: IconRiTwitterXLine,
			isAlwaysVisible: false
		},
		{
			value: urls.facebook,
			label: SHARE_LABELS.FACEBOOK,
			icon: IconRiFacebookCircleLine,
			isAlwaysVisible: false
		},
		{
			value: urls.email,
			label: SHARE_LABELS.EMAIL,
			icon: IconMdiEmailOutline,
			isAlwaysVisible: false
		}
	];
});

const { trackCtaViewMoreClicked, trackShareCollectionClicked } = useEvents();

const alwaysVisibleOptions = computed(() =>
	sharingOptions.value.filter(opt => opt.isAlwaysVisible)
);

const expandableOptions = computed(() =>
	sharingOptions.value.filter(opt => !opt.isAlwaysVisible)
);

function handleCopyLink() {
	navigator?.clipboard?.writeText(shareUrl.value);
	modelValue.value = false;
	toast({
		description: h(CopyToastDescription)
	});

	trackShareCollectionClicked({
		[MixpanelProps.SHARE_WHATSAPP]: false,
		[MixpanelProps.SHARE_FACEBOOK]: false,
		[MixpanelProps.SHARE_EMAIL]: false,
		[MixpanelProps.SHARE_TWITTER]: false,
		[MixpanelProps.COPY_LINK]: true,
		[MixpanelProps.PROPERTY_COUNT]: props.properties?.length || 0,
		[MixpanelProps.SECTION_NAME]: props.sectionName,
		[MixpanelProps.CURRENT_PAGE]: route.fullPath
	});
}

const route = useRoute();
function handleShareClick(option: SharingOption) {
	if (option.isCopyAction) {
		handleCopyLink();
		return;
	}
	trackShareCollectionClicked({
		[MixpanelProps.SHARE_WHATSAPP]: option.label === SHARE_LABELS.WHATSAPP,
		[MixpanelProps.SHARE_FACEBOOK]: option.label === SHARE_LABELS.FACEBOOK,
		[MixpanelProps.SHARE_EMAIL]: option.label === SHARE_LABELS.EMAIL,
		[MixpanelProps.SHARE_TWITTER]: option.label === SHARE_LABELS.TWITTER,
		[MixpanelProps.COPY_LINK]: false,
		[MixpanelProps.PROPERTY_COUNT]: props.properties?.length || 0,
		[MixpanelProps.SECTION_NAME]: props.sectionName,
		[MixpanelProps.CURRENT_PAGE]: route.fullPath
	});

	window.open(option.value, '_blank', 'noopener,noreferrer');
}

function handleToggleOptions(isExpanded: boolean, toggle: () => void) {
	if (!isExpanded) {
		trackCtaViewMoreClicked({
			[MixpanelProps.CURRENT_FLOW]: props.sectionName,
			[MixpanelProps.CTA_TEXT]: 'Mostrar mais opções'
		});
	}
	toggle();
}
// Toggle handled by ToggleContainer

watch(modelValue, isOpen => {
	if (import.meta.client) {
		if (isOpen) {
			document.body.style.overflow = 'hidden';
		} else {
			document.body.style.overflow = '';
		}
	}
});

onUnmounted(() => {
	if (import.meta.client) {
		document.body.style.overflow = '';
	}
});
</script>

<template>
	<Dialog v-model:open="modelValue">
		<DialogContent class="max-w-[600px] bg-white p-0">
			<DialogHeader class="border-b border-neutral-200 p-5">
				<DialogTitle
					class="font-matter-sq text-2xl font-normal tracking-[-0.48px] text-black"
				>
					{{ title }}
				</DialogTitle>
			</DialogHeader>

			<div class="px-6 pb-4 pt-6">
				<!-- gap entre o header e o conteúdo: 24px -->
				<div class="mb-6 flex items-center gap-4 pl-1.5 pr-11">
					<StackedPropertyImages :images="propertyImages" />

					<!-- Description Text -->
					<div class="flex-1 text-base font-normal leading-6 text-black">
						{{ description }}
					</div>
				</div>

				<!-- Sharing Options -->
				<div class="mb-7 w-full max-w-[552px]">
					<!-- Always Visible Options - gap entre botões: 12px -->
					<div
						class="mb-3 flex flex-col gap-3 sm:flex-row sm:flex-wrap sm:items-start"
					>
						<Button
							v-for="option in alwaysVisibleOptions"
							:key="option.label"
							variant="secondary"
							size="sm"
							class="flex h-10 w-full items-center justify-start gap-2 sm:w-[270px]"
							@click="handleShareClick(option)"
						>
							<Component :is="option.icon" class="size-5 text-black" />
							<span class="text-sm font-medium leading-5 text-black">
								{{ option.label }}
							</span>
						</Button>
					</div>

					<!-- Expanded Options -->
					<ToggleContainer
						:container-class="'w-full'"
						:content-class="'mb-3 flex flex-col gap-3 sm:flex-row sm:flex-wrap sm:items-start'"
						:button-class="'flex h-[23px] cursor-pointer items-center gap-2 p-0'"
						expand-text="Mostrar mais opções"
						collapse-text="Mostrar menos opções"
					>
						<template #content>
							<Button
								v-for="option in expandableOptions"
								:key="option.label"
								variant="secondary"
								size="sm"
								class="flex h-10 w-full items-center justify-start gap-2 sm:w-[270px]"
								@click="handleShareClick(option)"
							>
								<Component :is="option.icon" class="size-5 text-black" />
								<span class="text-sm font-medium leading-5 text-black">
									{{ option.label }}
								</span>
							</Button>
						</template>

						<template #button="{ isExpanded, toggle }">
							<button
								class="flex h-[23px] cursor-pointer items-center gap-2 p-0"
								@click.stop="handleToggleOptions(isExpanded, toggle)"
							>
								<Component
									:is="
										isExpanded
											? IconMaterialSymbolsExpandLess
											: IconMaterialSymbolsExpandMore
									"
									class="size-5 text-black"
								/>
								<div class="flex flex-col gap-0.5">
									<span
										class="text-sm font-normal leading-[150%] text-black underline"
									>
										{{
											isExpanded
												? 'Mostrar menos opções'
												: 'Mostrar mais opções'
										}}
									</span>
								</div>
							</button>
						</template>
					</ToggleContainer>
				</div>

				<div class="mt-7 text-center">
					<p class="text-xs leading-6 text-gray-600">
						{{ footer }}
					</p>
				</div>
			</div>
		</DialogContent>
	</Dialog>
</template>
